/**
 * Tâche 1445 (Eric 2026-10-05) — « Laisse l'IA proposer d'elle-même les termes
 * cibles à surveiller, à partir des mots qui reviennent mal écrits. »
 *
 * Trois choses que cet écran DIT, et qui ne vont pas de soi :
 *   1. **sur quoi** la recherche a porté (nombre de documents lus par zone) —
 *      un chiffre sans son assiette ne veut rien dire (acquis 1400) ;
 *   2. **pourquoi** l'IA pense que c'est une faute, en une phrase ;
 *   3. **ce qui a été écarté ne revient plus** — et peut être remis dans le
 *      jeu, parce qu'on peut se tromper.
 */
import { useState } from "react";
import {
  ArrowCounterClockwise, Check, Sparkle, SpinnerGap, X,
} from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const LIBELLE_ORIGINE = {
  correction_consignee: ["Correction déjà appliquée dans une transcription",
                         "Fix already applied in a transcript"],
  quasi_sosie: ["Ressemble de très près à un terme du cabinet",
                "Very close to one of the firm's terms"],
};

export const PropositionsIA = ({ donnees, chargement, ecartees, onLancer,
                                 onAccepter, onEcarter, onReconsiderer }) => {
  const { lang, tr } = useLang();
  const [etats, setEtats] = useState({});

  const agir = async (prop, action) => {
    setEtats((e) => ({ ...e, [prop.cle]: "en_cours" }));
    const ok = action === "accepter"
      ? await onAccepter(prop)
      : await onEcarter(prop);
    setEtats((e) => ({ ...e, [prop.cle]: ok ? action : null }));
  };

  const lus = donnees?.documents_lus || {};
  const propositions = donnees?.propositions || [];

  return (
    <div data-testid="orthographe-propositions">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          type="button"
          onClick={onLancer}
          disabled={chargement}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-violet-700 text-white text-sm font-medium hover:bg-violet-800 transition-colors disabled:opacity-40"
          data-testid="orthographe-lancer-propositions"
        >
          {chargement
            ? <SpinnerGap size={16} className="animate-spin" />
            : <Sparkle size={16} />}
          {tr("Chercher les termes à surveiller", "Look for terms worth watching")}
        </button>
        <span className="text-[11px] text-zinc-500">
          {tr("L'IA lit les transcriptions, les Q&R, les synthèses et les scripts de vente, puis vous soumet ce qu'elle croit être des fautes récurrentes. Rien n'est ajouté sans votre accord.",
              "The AI reads transcripts, Q&A, syntheses and sales scripts, then submits what it believes are recurring mistakes. Nothing is added without your approval.")}
        </span>
      </div>

      {donnees && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-[11px] text-zinc-600"
             data-testid="orthographe-propositions-assiette">
          <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200">
            {tr("Transcriptions lues", "Transcripts read")} : {lus.transcription || 0}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200">
            {tr("Q&R et synthèses", "Q&A and syntheses")} : {lus.connaissance || 0}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200">
            {tr("Scripts de vente", "Sales scripts")} : {lus.script_de_vente || 0}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200">
            {tr("Vocabulaire du cabinet", "Firm vocabulary")} : {donnees.vocabulaire || 0}
          </span>
          {donnees.candidats_soumis != null && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200">
              {tr("Pistes soumises à l'IA", "Leads submitted to the AI")} : {donnees.candidats_soumis}
            </span>
          )}
        </div>
      )}

      {donnees && propositions.length === 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-6 text-center mb-4"
             data-testid="orthographe-propositions-vide">
          <p className="text-sm text-zinc-600">
            {donnees.motif || tr(
              "L'IA n'a retenu aucune faute récurrente. Les pistes trouvées étaient des pluriels, des majuscules ou des mots français corrects.",
              "The AI kept no recurring mistake. The leads found were plurals, capitals or correct French words.")}
          </p>
        </div>
      )}

      <div className="space-y-2 mb-6">
        {propositions.map((p) => {
          const etat = etats[p.cle];
          if (etat === "accepter" || etat === "ecarter") {
            return (
              <div key={p.cle}
                   className="border border-zinc-200 bg-zinc-50 rounded-xl px-4 py-2.5 text-xs text-zinc-500"
                   data-testid={`orthographe-proposition-traitee-${p.cle}`}>
                {etat === "accepter"
                  ? tr(`« ${p.faute} » est désormais surveillé pour « ${p.terme} ».`,
                       `"${p.faute}" is now watched for "${p.terme}".`)
                  : tr(`« ${p.faute} » écarté — il ne sera plus proposé.`,
                       `"${p.faute}" discarded — it will not be suggested again.`)}
              </div>
            );
          }
          const origine = LIBELLE_ORIGINE[p.origine];
          return (
            <div key={p.cle} className="border border-zinc-200 bg-white rounded-xl p-4"
                 data-testid={`orthographe-proposition-${p.cle}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="text-sm font-semibold text-rose-700 break-words">{p.faute}</span>
                <span className="text-zinc-400">→</span>
                <span className="text-sm font-semibold text-emerald-700 break-words">{p.terme}</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200 whitespace-nowrap">
                  {p.occurrences} {tr("fois", p.occurrences === 1 ? "time" : "times")}
                </span>
                <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${
                  p.confiance === "haute"
                    ? "bg-violet-50 text-violet-800 border-violet-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}>
                  {p.confiance === "haute"
                    ? tr("confiance haute", "high confidence")
                    : tr("confiance moyenne", "medium confidence")}
                </span>
              </div>
              {p.raison && (
                <p className="text-xs text-zinc-600 mb-1">{p.raison}</p>
              )}
              {origine && (
                <p className="text-[11px] text-zinc-400 mb-2">
                  {lang === "en" ? origine[1] : origine[0]}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => agir(p, "accepter")}
                  disabled={etat === "en_cours"}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#002FA7] text-white text-[11px] font-medium hover:bg-[#001f7a] transition-colors disabled:opacity-50"
                  data-testid={`orthographe-proposition-accepter-${p.cle}`}
                >
                  {etat === "en_cours"
                    ? <SpinnerGap size={12} className="animate-spin" />
                    : <Check size={12} />}
                  {tr("C'est une faute — surveiller", "It is a mistake — watch it")}
                </button>
                <button
                  type="button"
                  onClick={() => agir(p, "ecarter")}
                  disabled={etat === "en_cours"}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                  data-testid={`orthographe-proposition-ecarter-${p.cle}`}
                >
                  <X size={12} />
                  {tr("Ce n'est pas une faute", "Not a mistake")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {ecartees.length > 0 && (
        <div className="border border-zinc-200 bg-white rounded-xl p-4"
             data-testid="orthographe-propositions-ecartees">
          <h3 className="text-sm font-semibold text-zinc-900 mb-1">
            {tr("Écartées — elles ne reviendront plus", "Discarded — they will not come back")}
          </h3>
          <p className="text-[11px] text-zinc-500 mb-3">
            {tr("Vous pouvez en remettre une dans le jeu : on peut se tromper.",
                "You can put one back in play: anyone can be wrong.")}
          </p>
          <div className="flex flex-wrap gap-2">
            {ecartees.map((e) => (
              <span key={e.cle}
                    className="inline-flex items-center gap-1 bg-zinc-50 border border-zinc-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs text-zinc-700">
                {e.faute} <span className="text-zinc-400">→</span> {e.terme}
                <button
                  type="button"
                  onClick={() => onReconsiderer(e.cle)}
                  className="p-1 rounded-full text-zinc-400 hover:text-[#002FA7] hover:bg-white"
                  data-testid={`orthographe-reconsiderer-${e.cle}`}
                  title={tr("Remettre dans le jeu", "Put back in play")}
                >
                  <ArrowCounterClockwise size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropositionsIA;
